import { useRef, useState } from 'react'
import { verifyFile, type VerificationReport } from '../api'
import { dateTime } from '../format'
import { bytesAddedAfter, issuerTrust, verdict, versionCovered } from '../verifyVerdict'

/**
 * Verify checks any PDF a visitor drops in, whether or not docsdone has ever
 * seen it. The signatures travel inside the document, so checking them does
 * not require us. When nobody this machine trusts signed a file, though, the
 * names on its signatures are only claims, and the page then says whether
 * docsdone recognises the exact file as one it sealed, and that its verdict
 * rests on that record rather than on a trusted certificate.
 */
export function Verify() {
  const [report, setReport] = useState<VerificationReport | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const input = useRef<HTMLInputElement>(null)

  async function check(file: File) {
    setBusy(true)
    setError('')
    setReport(null)
    setName(file.name)
    try {
      setReport(await verifyFile(file))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed.')
    } finally {
      setBusy(false)
    }
  }

  const lead = report && verdict(report)
  // The authorities behind the document timestamps, for a file that carries
  // nothing else.
  const stamps = report?.signers?.filter((s) => s.document_timestamp) ?? []
  const authorities = [...new Set(stamps.map((s) => s.certificate_subject).filter(Boolean))].join(', ')

  return (
    <main className="page page-narrow">
      <div className="page-head">
        <h1>Verify a document</h1>
        <p>
          Check whether the signatures in a PDF still match what they signed, and who made them.
          They are read from the file itself, so this works for documents we have never seen. We
          also look up whether docsdone sealed this exact file.
        </p>
      </div>

      <div
        className="dropzone"
        onClick={() => input.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          const f = e.dataTransfer.files?.[0]
          if (f) void check(f)
        }}
      >
        <strong>Drop a PDF here</strong>
        <span className="small">or click to choose a file</span>
        <input
          ref={input}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void check(f)
          }}
        />
      </div>

      {busy && <p className="muted file-name" style={{ marginTop: '1rem' }}>Checking {name}…</p>}
      {error && <div className="notice notice-bad" style={{ marginTop: '1rem' }}>{error}</div>}

      {report && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h2 className="file-name">{name}</h2>

          {lead === 'not_checked' ? (
            <div className="notice notice-warn">
              <strong>Not checked.</strong> This file carries more signatures than we check in one
              file, so we say nothing about it: not that it is intact, and not that it is not.
            </div>
          ) : lead === 'unsigned' ? (
            <div className="notice notice-warn">
              This PDF carries no digital signature we can read.
              {report.error ? ` (${report.error})` : ''}
            </div>
          ) : lead === 'timestamp_only' ? (
            <div className={report.intact ? 'notice notice-warn' : 'notice notice-bad'}>
              <strong>Timestamped, not signed.</strong> This file carries only{' '}
              {stamps.length === 1 ? 'a document timestamp' : `${stamps.length} document timestamps`} from{' '}
              {authorities || 'an authority we could not name'}. A timestamp shows these bytes
              existed at the time it names. It is not a signature, and says nothing about who
              wrote or approved them.
              {!report.intact && ' It does not match the bytes it covers, or cannot be checked, so it does not show even that.'}
            </div>
          ) : lead === 'added_after_trusted' || lead === 'added_after' ? (
            <div className="notice notice-warn">
              <strong>{bytesAddedAfter(lead).heading}</strong> {bytesAddedAfter(lead).body}
            </div>
          ) : lead === 'not_recognised' ? (
            <div className="notice notice-warn">
              <strong>Nobody this machine trusts signed this.</strong> Every signature still matches
              the bytes it signed, but none was made with a certificate this machine trusts, and
              docsdone does not recognise this exact file as one it sealed. The name on such a
              signature is whatever its holder wrote there, docsdone&rsquo;s included.
            </div>
          ) : lead === 'recognised' ? (
            <div className="notice notice-good">
              <strong>Sealed by docsdone.</strong> docsdone recognises this exact file as one it
              sealed, and every signature still matches the bytes it signed. Nobody this machine
              trusts signed it, so that is docsdone&rsquo;s own record speaking: outside docsdone,
              the signatures show the bytes are unchanged, not who made them.
            </div>
          ) : lead === 'intact' ? (
            <div className="notice notice-good">
              <strong>Intact.</strong> Every signature in this file still matches the bytes it
              signed.
            </div>
          ) : (
            <div className="notice notice-bad">
              <strong>Not intact.</strong> At least one signature does not match the bytes it
              signed, or cannot be checked.
            </div>
          )}

          {report.signers?.map((s, i) =>
            s.document_timestamp ? (
              <div key={i} style={{ marginTop: '1rem' }}>
                <h3>Document timestamp {i + 1}</h3>
                <table className="table table-pairs">
                  <tbody>
                    <tr><td className="muted">Stamped by</td><td className="mono">{s.certificate_subject || '—'}</td></tr>
                    <tr>
                      <td className="muted">Integrity</td>
                      <td>{s.valid_signature ? 'Valid: bytes unchanged since stamped' : 'Fails: does not match the bytes it covers, or cannot be checked'}</td>
                    </tr>
                    <tr><td className="muted">Authority trust</td><td>{issuerTrust(s)}</td></tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div key={i} style={{ marginTop: '1rem' }}>
                <h3>Signature {i + 1}</h3>
                <table className="table table-pairs">
                  <tbody>
                    <tr><td className="muted">Signed by</td><td>{s.name || '—'}</td></tr>
                    <tr><td className="muted">Reason</td><td>{s.reason || '—'}</td></tr>
                    <tr><td className="muted">Contact</td><td>{s.contact_info || '—'}</td></tr>
                    <tr><td className="muted">Signing time</td><td>{dateTime(s.signature_time)}</td></tr>
                    <tr><td className="muted">Certificate</td><td className="mono">{s.certificate_subject || '—'}</td></tr>
                    <tr>
                      <td className="muted">Integrity</td>
                      <td>{s.valid_signature ? 'Valid: bytes unchanged' : 'Fails: does not match the bytes it signed, or cannot be checked'}</td>
                    </tr>
                    <tr><td className="muted">Issuer trust</td><td>{issuerTrust(s)}</td></tr>
                    {versionCovered(s) && (
                      <tr><td className="muted">Covers</td><td>{versionCovered(s)}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ),
          )}

          {!!report.signers?.length && lead !== 'timestamp_only' && lead !== 'not_recognised' && lead !== 'recognised' &&
            report.intact && !report.trusted_chain && (
            <div className="notice notice-info" style={{ marginTop: '1rem' }}>
              <p>
                <strong>About issuer trust.</strong> Every signature matches the bytes it signed,
                but at least one was made with a certificate this machine does not trust: one that
                does not chain to a root it trusts, has expired, was not issued for signing
                documents, or that the file says was revoked. That proves the signed bytes are
                unchanged, not who held the key: the name on such a certificate is whatever its
                holder wrote there. Adobe Acrobat will show the same distinction.
              </p>
            </div>
          )}
        </div>
      )}
    </main>
  )
}
